import React, { useEffect, useMemo, useState } from 'react';

import { Modal, Form, Table, Tabs, Tab } from 'react-bootstrap';
import Button from 'components/base/Button';

import { useAppDispatch, useAppSelector } from 'redux/store';
import { createFolder, updateFolder, getFolders, getFolderAuditTrail, type Folder } from '../../../../redux/slices/lists/foldersSlice';
import { getAuthorizedZones } from '../../../../redux/slices/lists/zonesSlice';
import { useUnsavedChanges } from '../../../../hooks/useUnsavedChanges';
import ConfirmationModal from '../../../../components/common/ConfirmationModal';
import type { CreateTarget } from 'actions';

export interface EditFolderModalProps {
  show: boolean;
  handleClose: () => void;
  mode: 'create' | 'edit';
  data: Folder | null;
  // Workspace create pre-target: seeds the zone and the parent folder on create.
  createIn?: CreateTarget;
}

interface FolderFormState {
  name: string;
  description: string;
  zone_id: string;
  parent_folder_id: string;
  folder_id: number | null;
}

const emptyForm: FolderFormState = { name: '', description: '', zone_id: '', parent_folder_id: '', folder_id: null };

/**
 * Create / edit a folder. Self-contained: holds its own form state, fetches the
 * permission-filtered zone list and (on the Audit tab) the audit trail, and
 * persists via the folder thunks — so it can be opened from anywhere, not just
 * the list page.
 */
const EditFolderModal: React.FC<EditFolderModalProps> = ({ show, handleClose, mode, data, createIn }) => {
  const dispatch = useAppDispatch();
  const { zones } = useAppSelector((state) => state.listOfZones);
  const folders = useAppSelector((state) => state.listOfFolders.folders);
  const auditTrail = useAppSelector((state) => state.listOfFolders.auditTrail);

  const [form, setForm] = useState<FolderFormState>(emptyForm);
  const [key, setKey] = useState<'folder' | 'audit'>('folder');
  const [showCloseConfirm, setShowCloseConfirm] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);

  const isDirty = useUnsavedChanges(show, form);

  // Initialise the form whenever the dialog opens.
  useEffect(() => {
    if (!show) return;
    setKey('folder');
    if (mode === 'edit' && data) {
      setForm({
        name: data.name,
        description: data.description || '',
        zone_id: data.zone_id != null ? String(data.zone_id) : '',
        parent_folder_id: data.parent_folder_id != null ? String(data.parent_folder_id) : '',
        folder_id: data.folder_id,
      });
    } else {
      // Create: seed the zone (and, for a folder, the parent folder) from the
      // workspace create pre-target when present.
      setForm({
        ...emptyForm,
        zone_id: createIn ? String(createIn.zoneId) : '',
        parent_folder_id: createIn?.folderId != null ? String(createIn.folderId) : '',
      });
    }
  }, [show, mode, data, createIn]);

  // Fetch the (permission-filtered) zones and the folder list for the parent
  // dropdown. Loading folders here (not just relying on the list page having
  // populated the slice) is what makes a parent pre-filled from the Workspace
  // actually show its option.
  useEffect(() => {
    if (show) {
      dispatch(getAuthorizedZones({ resourceType: 'folders', action: mode === 'edit' ? 'update' : 'create' }) as any);
      dispatch(getFolders() as any);
    }
  }, [dispatch, show, mode]);

  // Fetch the audit trail when switching to the Audit tab.
  useEffect(() => {
    if (key === 'audit' && form.folder_id) {
      dispatch(getFolderAuditTrail(form.folder_id) as any);
    }
  }, [key, form.folder_id, dispatch]);

  const handleChange = (e: React.ChangeEvent<any>) => {
    const { name, value } = e.target;
    // Changing the zone clears the (zone-scoped) parent folder.
    setForm((prev) => (name === 'zone_id' ? { ...prev, zone_id: value, parent_folder_id: '' } : { ...prev, [name]: value }));
  };

  const filteredParentFolders = useMemo(() => {
    if (!form.zone_id || !folders) return [];
    return folders.filter((f) => f.zone_id === Number(form.zone_id) && f.folder_id !== form.folder_id);
  }, [folders, form.zone_id, form.folder_id]);

  const resetAndClose = () => {
    setForm(emptyForm);
    handleClose();
  };

  const handleRequestClose = () => {
    if (isDirty) {
      setShowCloseConfirm(true);
    } else {
      resetAndClose();
    }
  };

  const handleSave = async () => {
    const payload = {
      name: form.name,
      description: form.description,
      zone_id: form.zone_id ? Number(form.zone_id) : undefined,
      parent_folder_id: form.parent_folder_id ? Number(form.parent_folder_id) : null,
    };
    setIsSubmitting(true);
    try {
      if (form.folder_id) {
        const result = await dispatch(updateFolder({ ...payload, folder_id: form.folder_id }));
        if (updateFolder.rejected.match(result)) return;
      } else {
        const result = await dispatch(createFolder(payload));
        if (createFolder.rejected.match(result)) return;
      }
      resetAndClose();
    } finally {
      setIsSubmitting(false);
    }
  };

  const folderForm = (
    <Form>
      <Form.Group className="mb-3">
        <Form.Label>Name</Form.Label>
        <Form.Control type="text" placeholder="Enter folder name" name="name" value={form.name} onChange={handleChange} required />
      </Form.Group>
      <Form.Group className="mb-3">
        <Form.Label>Description</Form.Label>
        <Form.Control type="text" placeholder="Enter folder description" name="description" value={form.description} onChange={handleChange} />
      </Form.Group>
      <Form.Group className="mb-3">
        <Form.Label>Zone</Form.Label>
        <Form.Select name="zone_id" value={form.zone_id} onChange={handleChange} required>
          <option value="">Select a zone</option>
          {(zones || []).map((zone: any) => (
            <option key={zone.zone_id} value={zone.zone_id}>
              {zone.name}
            </option>
          ))}
        </Form.Select>
      </Form.Group>
      <Form.Group className="mb-3">
        <Form.Label>Parent Folder</Form.Label>
        <Form.Select name="parent_folder_id" value={form.parent_folder_id} onChange={handleChange}>
          <option value="">None</option>
          {filteredParentFolders.map((folder) => (
            <option key={folder.folder_id} value={folder.folder_id}>
              {folder.name}
            </option>
          ))}
        </Form.Select>
      </Form.Group>
    </Form>
  );

  return (
    <>
      <Modal show={show} onHide={handleRequestClose} className={mode === 'edit' ? 'modal-lg' : undefined}>
        <Modal.Header closeButton>
          <Modal.Title>{mode === 'edit' ? 'Edit Folder' : 'Create New Folder'}</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          {mode === 'edit' ? (
            <Tabs id="folder-audit-tabs" activeKey={key} onSelect={(k) => setKey(k as 'folder' | 'audit')} className="mb-3">
              <Tab eventKey="folder" title="Folder">
                {folderForm}
              </Tab>
              <Tab eventKey="audit" title="Audit Trail">
                {auditTrail && auditTrail?.audit_trail?.length > 0 ? (
                  <Table striped bordered hover>
                    <thead>
                      <tr>
                        <th>Timestamp</th>
                        <th>Type</th>
                        <th>User</th>
                        <th>Details</th>
                      </tr>
                    </thead>
                    <tbody>
                      {auditTrail?.audit_trail?.map((audit: any, index: number) => (
                        <tr key={index}>
                          <td>{audit.created_at}</td>
                          <td>{audit.audit_details.action}</td>
                          <td>{audit.user_name}</td>
                          <td>
                            <b>Previous state:</b> <br />
                            Name: {audit.audit_details.previous_state?.name} <br />
                            Description: {audit.audit_details.previous_state?.description} <br />
                            <b>New state:</b> <br />
                            Name: {audit.audit_details.new_state?.name} <br />
                            Description: {audit.audit_details.new_state?.description}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </Table>
                ) : (
                  <p>No audit trail available</p>
                )}
              </Tab>
            </Tabs>
          ) : (
            folderForm
          )}
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={handleRequestClose}>
            Close
          </Button>
          <Button variant="primary" onClick={handleSave} disabled={isSubmitting}>
            {mode === 'edit' ? 'Save and Close' : 'Create Folder'}
          </Button>
        </Modal.Footer>
      </Modal>

      <ConfirmationModal
        show={showCloseConfirm}
        title="Unsaved Changes"
        body="You have unsaved changes. Are you sure you want to close without saving?"
        confirmText="Discard Changes"
        confirmVariant="danger"
        onConfirm={() => {
          setShowCloseConfirm(false);
          resetAndClose();
        }}
        onCancel={() => setShowCloseConfirm(false)}
      />
    </>
  );
};

export default EditFolderModal;
